<template>
    <div
        class="flex flex-col justify-between items-center w-full p-6 pb-8"
        :style="{
            height: `${wrapperHeight}px`,
        }"
    >
        <Logo
            class="mx-auto text-white"
            :style="{
                height: `${logoHeight}px`,
                width: `${logoWidth}px`,
            }"
        />
        <transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="translate-y-44 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-44 opacity-0"
        >
            <div
                v-if="!action"
                class="flex flex-col items-center justify-center gap-10 w-full"
            >
                <div class="flex flex-col gap-4 items-center w-full px-2">
                    <ButtonWrapper
                        v-for="cta in CTAs"
                        :key="cta.key"
                        class="w-full h-12"
                        rounded-class="rounded-2.5"
                        :class="{
                            'border-white border-2': cta.key === 'login',
                        }"
                        :color="cta.key === 'login' ? 'transparent' : 'promo'"
                        :text-classes="{ 'text-white': cta.key === 'login' }"
                        @click="cta.action"
                    >
                        {{ cta.text }}
                    </ButtonWrapper>
                </div>

                <i18n-t
                    keypath="auth.register.term_condition"
                    tag="span"
                    class="text-label-xs text-center text-white"
                >
                    <template #agb>
                        <button class="underline" @click="redirectTo('/agb/')">
                            {{ $t("page.ls_page_agb") }}
                        </button>
                    </template>
                    <template #privacy>
                        <button class="underline" @click="redirectTo('/data/')">
                            {{ $t("page.ls_page_privacy_policy") }}
                        </button>
                    </template>
                </i18n-t>
            </div>
        </transition>
        <transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="translate-y-44 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-44 opacity-0"
        >
            <SocialLogins
                v-if="showSocial"
                :action="action"
                @proceed-with-email="emit('selected-action', action)"
                @selected-social-login="handleSelectedSocialLogin"
                @reset-action="handleReset"
            />
        </transition>
    </div>
</template>

<script setup>
import SocialLogins from "@/Components/Widgets/PWA/SocialLogins.vue";
import ButtonWrapper from "@/Components/Buttons/ButtonWrapper.vue";
import Logo from "@/Components/Logo.vue";
import { getRedirectUrl } from "@/Helpers/utils";
import { useI18n } from "vue-i18n";
import { computed, onBeforeMount, onUnmounted, ref } from "vue";
import { usePage } from "@inertiajs/vue3";

const props = defineProps({
    socialLoginEnabled: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(["selected-action", "selected-social-login"]);
const { t } = useI18n();
const wrapperHeight = ref(0);
const logoHeight = ref(0);
const logoWidth = ref(0);
const action = ref(null);
const showSocial = ref(false);

onBeforeMount(() => {
    resizeHandler();
    window.addEventListener("resize", resizeHandler);
});

onUnmounted(() => {}),
    () => {
        window.removeEventListener("resize", resizeHandler);
    };
const resizeHandler = () => {
    wrapperHeight.value = window.innerHeight - window.innerHeight / 5;
    logoWidth.value = window.innerWidth / 2;
    logoHeight.value = logoWidth.value / 2.31;
};

const CTAs = computed(() => {
    return [
        {
            key: "register",
            text: t("auth.register.title"),
            action: () => {
                handleActionCTAClick("register");
            },
        },
        {
            key: "login",
            text: t("auth.login.title"),
            action: () => {
                handleActionCTAClick("login");
            },
        },
    ];
});

const handleActionCTAClick = (followUpAction) => {
    action.value = followUpAction;

    setTimeout(() => {
        showSocial.value = props.socialLoginEnabled;

        if (!showSocial.value) {
            emit("selected-action", action.value);
        }
    }, 300);
};

const handleSelectedSocialLogin = (driver) => {
    emit("selected-social-login", driver);
    emit("selected-action", action.value);
};

const handleReset = () => {
    showSocial.value = false;

    setTimeout(() => {
        action.value = null;
        emit("selected-action", action.value);
    }, 300);
};

const redirectTo = (url) => {
    const redirectUrl = getRedirectUrl(url);
    const external = true;

    if (usePage().props.isMobileApp) {
        window.natively.openExternalURL(redirectUrl, external);
    } else {
        window.open(url, "_blank").focus();
    }
};
</script>
